import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { ListPaginationMeta } from "utils/helpers/pagination";

import {
  AddOrderDropDownKey,
  AddOrderForm,
  AddOrderModalContext,
} from "../types";
import {
  initialAddOrderForm,
  initialDropDownState,
  initialState,
} from "./initialState";

export const sliceKey = "addOrderModalState";

type DropDownSuccessPayload = {
  key: AddOrderDropDownKey;
  data: string[];
  meta: ListPaginationMeta;
};

const resetDropDowns = () => ({
  customers: { ...initialDropDownState },
  manufacturers: { ...initialDropDownState },
  models: { ...initialDropDownState },
});

const addOrderModalSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    openAddOrderModal(
      state,
      action: PayloadAction<{
        context: AddOrderModalContext;
        customerName?: string;
      }>
    ) {
      state.isOpen = true;
      state.context = action.payload.context;
      state.form = {
        ...initialAddOrderForm,
        customerName: action.payload.customerName || "",
      };
      state.dropDowns = resetDropDowns();
    },

    closeAddOrderModal(state) {
      state.isOpen = false;
      state.form = initialAddOrderForm;
      state.dropDowns = resetDropDowns();
    },

    getDropDownList(state, action: PayloadAction<AddOrderDropDownKey>) {
      const key = action.payload;
      state.dropDowns[key] = {
        ...initialDropDownState,
        perPage: state.dropDowns[key].perPage,
        loading: true,
      };
    },

    getDropDownListSuccess(
      state,
      action: PayloadAction<DropDownSuccessPayload>
    ) {
      const { key, data, meta } = action.payload;
      let items = data;

      if (key === "customers") {
        const selectedName = state.form.customerName;
        if (selectedName && !items.includes(selectedName)) {
          items = [selectedName, ...items];
        }
      }

      state.dropDowns[key] = {
        ...state.dropDowns[key],
        items,
        meta,
        page: meta.currentPage,
        loading: false,
        error: null,
      };
    },

    getDropDownListError(
      state,
      action: PayloadAction<{ key: AddOrderDropDownKey; message: string }>
    ) {
      const { key, message } = action.payload;
      state.dropDowns[key].loading = false;
      state.dropDowns[key].error = message;
    },

    getDropDownNextPage(state, action: PayloadAction<AddOrderDropDownKey>) {
      const dropDown = state.dropDowns[action.payload];
      if (
        dropDown.loading ||
        dropDown.meta.currentPage >= dropDown.meta.totalPages
      ) {
        return;
      }
      dropDown.loading = true;
      dropDown.page = dropDown.meta.currentPage + 1;
    },

    getDropDownNextPageSuccess(
      state,
      action: PayloadAction<DropDownSuccessPayload>
    ) {
      const { key, data, meta } = action.payload;
      const dropDown = state.dropDowns[key];
      dropDown.loading = false;
      dropDown.items = dropDown.items.concat(data);
      dropDown.meta = meta;
      dropDown.page = meta.currentPage;
      dropDown.error = null;
    },

    getDropDownNextPageError(
      state,
      action: PayloadAction<{ key: AddOrderDropDownKey; message: string }>
    ) {
      const { key, message } = action.payload;
      state.dropDowns[key].loading = false;
      state.dropDowns[key].error = message;
    },

    setFormField(
      state,
      action: PayloadAction<{ name: keyof AddOrderForm; value: string }>
    ) {
      state.form[action.payload.name] = action.payload.value;
    },
  },
});

export const { actions: addOrderModalActions, reducer } = addOrderModalSlice;
